import { createFormHook, createFormHookContexts, useSelector } from '@tanstack/react-form'
import { useMemo } from 'react'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { GhostButton } from '../../components/buttons/ghost-button'
import { RadioGroup, RadioItem } from '../../components/input/radio.tsx'
import { SearchPicker } from '../../components/input/search-picker.tsx'
import { SelectBox } from '../../components/input/select-box.tsx'
import { TextInput } from '../../components/input/text-input.tsx'
import { SCMIcon } from '../../components/third-party-branding/scm-icon.tsx'
import {
  useAllIntegrations,
  useAzureProjectSearch,
  useAzureRepositorySearch,
  useBitbucketRepositorySearch,
  useBitbucketWorkspaceSearch,
  useGetAzureOrganizations,
  useGitLabProjectSearch,
} from '../../utils/api-client/api-hooks.ts'
import type {
  AddRepositoryRequest,
  AzureIntegrationRepository,
  AzureProject,
  BitbucketWorkspace,
  GitLabProjectResponse,
  Integration,
  IntegrationsBitbucketRepositoryResponse,
} from '../../utils/api-client/api-schemas'
import { getPickerErrorMessage } from '../../utils/error-handling/utils.ts'
import type { EnvironmentName } from '../../utils/hooks/get-environment-data.ts'
import * as styles from './add-repository-form.css'

export type ConnectionType = 'GitHubApp' | 'GenericGit' | 'Integration'
export type IntegrationType = 'azureDevOps' | 'gitlab' | 'bitbucket'

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

type AddRepositoryFormValues = {
  selectedConnectionType: ConnectionType
  selectedIntegration: IntegrationType
  url: string
  username: string
  password: string
  selectedGitHubIntegrationId: string | undefined
  selectedAzureIntegrationId: string | undefined
  azurePicks: AzurePicks | undefined
  selectedGitLabIntegrationId: string | undefined
  gitLabPicks: GitLabPicks | undefined
  selectedBitbucketIntegrationId: string | undefined
  bitbucketPicks: BitbucketPicks | undefined
}

type AzurePicks = {
  integrationId: string
  organizationName: string
  project?: AzureProject
  repository?: AzureIntegrationRepository
}

type GitLabPicks = {
  integrationId: string
  project?: GitLabProjectResponse
}

type BitbucketPicks = {
  integrationId: string
  workspace?: BitbucketWorkspace
  repository?: IntegrationsBitbucketRepositoryResponse
}

const FORM_DEFAULT_VALUES: AddRepositoryFormValues = {
  selectedConnectionType: 'GitHubApp',
  selectedIntegration: 'azureDevOps',
  url: '',
  username: '',
  password: '',
  selectedGitHubIntegrationId: undefined,
  selectedAzureIntegrationId: undefined,
  azurePicks: undefined,
  selectedGitLabIntegrationId: undefined,
  gitLabPicks: undefined,
  selectedBitbucketIntegrationId: undefined,
  bitbucketPicks: undefined,
}

export const useAddRepositoryForm = (defaultValueOverrides: Partial<AddRepositoryFormValues>) =>
  useAppForm({
    defaultValues: {
      ...FORM_DEFAULT_VALUES,
      ...defaultValueOverrides,
    },
  })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

export type AddRepositoryFormProps = {
  environmentName: EnvironmentName
  onClose: () => unknown
  githubAppName: string | undefined
  isAzureIntegrationEnabled: boolean
  isGitlabIntegrationEnabled: boolean
  isBitbucketIntegrationEnabled: boolean
  addRepository: (params: AddRepositoryRequest, options?: { onSuccess?: () => unknown }) => unknown
}

export const AddRepositoryForm: React.FC<AddRepositoryFormProps> = ({
  environmentName,
  onClose,
  githubAppName,
  isAzureIntegrationEnabled,
  isGitlabIntegrationEnabled,
  isBitbucketIntegrationEnabled,
  addRepository,
}) => {
  const form = useFormContext()
  const selectedConnectionType = useSelector(form.store, s => s.values.selectedConnectionType)

  return (
    <form className={styles.form}>
      <ConnectionTypeRadioInput
        environmentName={environmentName}
        isGitHubIntegrationEnabled={!!githubAppName}
        isAzureIntegrationEnabled={isAzureIntegrationEnabled}
        isGitlabIntegrationEnabled={isGitlabIntegrationEnabled}
        isBitbucketIntegrationEnabled={isBitbucketIntegrationEnabled}
      />
      {match(selectedConnectionType)
        .with('GitHubApp', () =>
          githubAppName ? <PixeebotAppConnector onClose={onClose} githubAppName={githubAppName} /> : null
        )
        .with('GenericGit', () => (
          <>
            <div className={styles.inputGroup}>
              <GenericGitFields />
            </div>
            <div className={styles.buttonGroup}>
              <GhostButton onClick={onClose}>Cancel</GhostButton>
              <GenericGitConnectButton addRepository={addRepository} />
            </div>
          </>
        ))
        .with('Integration', () =>
          isAzureIntegrationEnabled || isGitlabIntegrationEnabled || isBitbucketIntegrationEnabled ? (
            <IntegrationConnector
              onClose={onClose}
              isAzureIntegrationEnabled={isAzureIntegrationEnabled}
              isGitlabIntegrationEnabled={isGitlabIntegrationEnabled}
              isBitbucketIntegrationEnabled={isBitbucketIntegrationEnabled}
              addRepository={addRepository}
            />
          ) : null
        )
        .exhaustive()}
    </form>
  )
}

type ConnectionTypeRadioInputProps = Pick<
  AddRepositoryFormProps,
  'environmentName' | 'isAzureIntegrationEnabled' | 'isGitlabIntegrationEnabled' | 'isBitbucketIntegrationEnabled'
> & {
  isGitHubIntegrationEnabled: boolean
}

const ConnectionTypeRadioInput: React.FC<ConnectionTypeRadioInputProps> = ({
  environmentName,
  isGitHubIntegrationEnabled,
  isAzureIntegrationEnabled,
  isGitlabIntegrationEnabled,
  isBitbucketIntegrationEnabled,
}) => {
  const form = useFormContext()
  const selectedConnectionType = useSelector(form.store, s => s.values.selectedConnectionType)

  return (
    <RadioGroup
      value={selectedConnectionType}
      onValueChange={type => form.setFieldValue('selectedConnectionType', type as ConnectionType)}
      className={styles.radioGroup}
    >
      {isGitHubIntegrationEnabled && (
        <RadioItem
          value="GitHubApp"
          id="github-app"
          label={`Install the ${environmentName === 'ntt' ? 'Github' : 'Pixeebot GitHub'} application (GitHub repositories only)`}
        />
      )}
      <RadioItem value="GenericGit" id="generic-git" label="Connect via Git client (clone a repository from any SCM)" />
      {(isAzureIntegrationEnabled || isGitlabIntegrationEnabled || isBitbucketIntegrationEnabled) && (
        <RadioItem value="Integration" id="integration" label="Connect to repositories from an existing integration" />
      )}
    </RadioGroup>
  )
}

type PixeebotAppConnectorProps = Pick<AddRepositoryFormProps, 'onClose' | 'githubAppName'>

const PixeebotAppConnector: React.FC<PixeebotAppConnectorProps> = ({ onClose, githubAppName }) => {
  const form = useFormContext()
  const selectedGitHubIntegrationId = useSelector(form.store, s => s.values.selectedGitHubIntegrationId)
  const { connections, selectedId } = useConnectionPicks('github', selectedGitHubIntegrationId)

  // Which App the installation link points at. A GitHub repository is not picked from a list the
  // way the other three providers' are: it arrives when someone installs the App, so the choice a
  // user makes here is which App to install, and each connection is a different App.
  const selected = connections.find(connection => connection.id === selectedId)
  const appName = (selected?.type === 'github' ? selected.app_name : undefined) ?? githubAppName

  return (
    <>
      <div className={styles.inputGroup}>
        <ConnectionSelect
          label="GitHub connection"
          connections={connections}
          selectedId={selectedId}
          onSelect={value => form.setFieldValue('selectedGitHubIntegrationId', value)}
        />
      </div>
      <div className={styles.buttonGroup}>
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        <DefaultButton
          href={`https://github.com/apps/${appName}/installations/new`}
          buttonType="primary"
          onClick={onClose}
        >
          Go to GitHub
        </DefaultButton>
      </div>
    </>
  )
}

type IntegrationConnectorProps = Pick<
  AddRepositoryFormProps,
  | 'onClose'
  | 'isAzureIntegrationEnabled'
  | 'isGitlabIntegrationEnabled'
  | 'isBitbucketIntegrationEnabled'
  | 'addRepository'
>

const IntegrationConnector: React.FC<IntegrationConnectorProps> = ({
  onClose,
  isAzureIntegrationEnabled,
  isGitlabIntegrationEnabled,
  isBitbucketIntegrationEnabled,
  addRepository,
}) => {
  const form = useFormContext()
  const selectedIntegration = useSelector(form.store, s => s.values.selectedIntegration)

  const integrationOptions = useMemo(() => {
    function* generateOptions() {
      if (isAzureIntegrationEnabled) {
        yield {
          value: 'azureDevOps' as const,
          label: (
            <span className={styles.selectItemText}>
              <SCMIcon type="azure" size="medium" />
              Azure DevOps
            </span>
          ),
        }
      }
      if (isGitlabIntegrationEnabled) {
        yield {
          value: 'gitlab' as const,
          label: (
            <span className={styles.selectItemText}>
              <SCMIcon type="gitlab" size="medium" />
              GitLab
            </span>
          ),
        }
      }
      if (isBitbucketIntegrationEnabled) {
        yield {
          value: 'bitbucket' as const,
          label: (
            <span className={styles.selectItemText}>
              <SCMIcon type="bitbucket" size="medium" />
              Bitbucket
            </span>
          ),
        }
      }
    }
    return [...generateOptions()]
  }, [isAzureIntegrationEnabled, isGitlabIntegrationEnabled, isBitbucketIntegrationEnabled])

  return (
    <>
      <div className={styles.inputGroup} aria-label="Integration selections">
        <SelectBox
          label="Integration"
          value={selectedIntegration}
          onValueChange={value => {
            form.setFieldValue('selectedIntegration', value as IntegrationType)
          }}
          options={integrationOptions}
        />
        {match(selectedIntegration)
          .with('azureDevOps', () => (isAzureIntegrationEnabled ? <AzureDevOpsFields /> : null))
          .with('gitlab', () => (isGitlabIntegrationEnabled ? <GitLabFields /> : null))
          .with('bitbucket', () => (isBitbucketIntegrationEnabled ? <BitbucketFields /> : null))
          .exhaustive()}
      </div>
      <div className={styles.buttonGroup}>
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        {match(selectedIntegration)
          .with('azureDevOps', () =>
            isAzureIntegrationEnabled ? <AzureDevOpsConnectButton addRepository={addRepository} /> : null
          )
          .with('gitlab', () =>
            isGitlabIntegrationEnabled ? <GitLabConnectButton addRepository={addRepository} /> : null
          )
          .with('bitbucket', () =>
            isBitbucketIntegrationEnabled ? <BitbucketConnectButton addRepository={addRepository} /> : null
          )
          .exhaustive()}
      </div>
    </>
  )
}

/**
 * The connection the form acts on and any picks made under it: the connection the picks were made
 * under, else the one the user chose, else the first listed. Picks keep their connection in effect,
 * so a refetched list that sorts another one first cannot move them, and they read as cleared once
 * that connection is no longer listed. A choice that is no longer listed falls back the same way.
 */
const useConnectionPicks = <TPicks extends { integrationId: string }>(
  type: Integration['type'],
  chosenId: string | undefined,
  picks?: TPicks
) => {
  const connections = useConnections(type)
  const keptId = [picks?.integrationId, chosenId].find(id => connections.some(connection => connection.id === id))
  const selectedId = keptId ?? connections[0]?.id
  return { connections, selectedId, picks: picks?.integrationId === selectedId ? picks : undefined }
}

const useAzurePicks = () => {
  const form = useFormContext()
  const chosenId = useSelector(form.store, s => s.values.selectedAzureIntegrationId)
  const picks = useSelector(form.store, s => s.values.azurePicks)
  return useConnectionPicks('azure', chosenId, picks)
}

const AzureDevOpsFields: React.FC = () => {
  const form = useFormContext()
  const { connections, selectedId, picks } = useAzurePicks()
  const organizationName = picks?.organizationName

  // A dropdown over one page with no search: a connection reaches exactly the organization it was
  // configured with, so there is never a second one to type for.
  const { data: azureOrganizationsPage, error: azureOrganizationsError } = useGetAzureOrganizations({
    integrationId: selectedId,
  })
  const azureOrganizationOptions = (azureOrganizationsPage?._embedded.items ?? []).map(org => ({
    value: org.name,
    label: org.name,
  }))

  return (
    <>
      <ConnectionSelect
        label="Azure DevOps connection"
        connections={connections}
        selectedId={selectedId}
        onSelect={value => {
          // The organizations, projects and repositories below all belong to the previous
          // connection, so the whole chain is cleared along with the choice.
          form.setFieldValue('selectedAzureIntegrationId', value)
          form.setFieldValue('azurePicks', undefined)
        }}
      />
      <SelectBox
        label="Organization"
        value={organizationName ?? ''}
        onValueChange={value =>
          form.setFieldValue(
            'azurePicks',
            selectedId === undefined ? undefined : { integrationId: selectedId, organizationName: value }
          )
        }
        placeholder="Select organization"
        options={azureOrganizationOptions}
        errorText={getPickerErrorMessage(azureOrganizationsError)}
      />
      <SearchPicker
        key={`${selectedId}/${organizationName}`}
        label="Project"
        placeholder={organizationName ? 'Search projects by name...' : 'Choose an organization first'}
        isDisabled={!organizationName}
        noun={{ singular: 'project', plural: 'projects' }}
        container="organization"
        initialValue={picks?.project}
        onChange={project =>
          form.setFieldValue('azurePicks', current => current && { ...current, project, repository: undefined })
        }
        useSearch={useAzureProjectSearch}
        searchInput={{ integrationId: selectedId, organizationName }}
        toOption={project => ({ value: project.id, label: project.name })}
      />
      <SearchPicker
        key={`${selectedId}/${organizationName}/${picks?.project?.id}`}
        label="Available repositories"
        placeholder={picks?.project ? 'Search repositories by name...' : 'Choose a project first'}
        isDisabled={!picks?.project}
        noun={{ singular: 'repository', plural: 'repositories' }}
        container="project"
        initialValue={picks?.repository}
        onChange={repository => form.setFieldValue('azurePicks', current => current && { ...current, repository })}
        useSearch={useAzureRepositorySearch}
        searchInput={{ integrationId: selectedId, organizationName, projectId: picks?.project?.id }}
        toOption={repository => ({
          value: repository.id,
          label: repository.name,
          enabled: repository.pixee_id === undefined,
        })}
      />
    </>
  )
}

type ConnectButtonProps = Pick<AddRepositoryFormProps, 'addRepository'>

const AzureDevOpsConnectButton: React.FC<ConnectButtonProps> = ({ addRepository }) => {
  const { selectedId, picks } = useAzurePicks()

  return (
    <DefaultButton
      buttonType="primary"
      state={picks?.repository ? 'default' : 'disabled'}
      onClick={() => {
        if (picks?.project && picks.repository) {
          addRepository({
            type: 'azure',
            organization: picks.organizationName,
            project: picks.project.name,
            name: picks.repository.name,
            // Which connection the repository was listed through. The backend clones from that
            // connection's server and records it, so every later call about this repository
            // reaches the same Azure DevOps.
            integration_id: selectedId,
          })
        }
      }}
    >
      Connect
    </DefaultButton>
  )
}

const useBitbucketPicks = () => {
  const form = useFormContext()
  const chosenId = useSelector(form.store, s => s.values.selectedBitbucketIntegrationId)
  const picks = useSelector(form.store, s => s.values.bitbucketPicks)
  return useConnectionPicks('bitbucket', chosenId, picks)
}

const BitbucketFields: React.FC = () => {
  const form = useFormContext()
  const { connections, selectedId, picks } = useBitbucketPicks()

  return (
    <>
      <ConnectionSelect
        label="Bitbucket connection"
        connections={connections}
        selectedId={selectedId}
        onSelect={value => {
          // The workspaces and repositories below belong to the previous connection's Atlassian
          // account, so both are cleared along with the choice.
          form.setFieldValue('selectedBitbucketIntegrationId', value)
          form.setFieldValue('bitbucketPicks', undefined)
        }}
      />
      <SearchPicker
        key={selectedId}
        label="Workspace"
        placeholder="Search workspaces by ID..."
        isDisabled={selectedId === undefined}
        noun={{ singular: 'workspace', plural: 'workspaces' }}
        initialValue={picks?.workspace}
        onChange={workspace =>
          form.setFieldValue(
            'bitbucketPicks',
            selectedId === undefined ? undefined : { integrationId: selectedId, workspace }
          )
        }
        useSearch={useBitbucketWorkspaceSearch}
        searchInput={{ integrationId: selectedId }}
        // Labelled by slug because that is what `search` matches: a field showing the name would
        // search for it once edited and could find nothing.
        toOption={workspace => ({ value: workspace.slug, label: workspace.slug })}
      />
      <SearchPicker
        key={`${selectedId}/${picks?.workspace?.slug}`}
        label="Repository"
        placeholder={picks?.workspace ? 'Search repositories by name...' : 'Choose a workspace first'}
        isDisabled={!picks?.workspace}
        noun={{ singular: 'repository', plural: 'repositories' }}
        container="workspace"
        initialValue={picks?.repository}
        onChange={repository => form.setFieldValue('bitbucketPicks', current => current && { ...current, repository })}
        useSearch={useBitbucketRepositorySearch}
        searchInput={{ integrationId: selectedId, workspaceSlug: picks?.workspace?.slug }}
        toOption={repository => ({
          value: repository.slug,
          label: repository.name,
          enabled: repository.pixee_id === undefined,
        })}
      />
    </>
  )
}

const BitbucketConnectButton: React.FC<ConnectButtonProps> = ({ addRepository }) => {
  const { selectedId, picks } = useBitbucketPicks()

  return (
    <DefaultButton
      buttonType="primary"
      state={picks?.repository ? 'default' : 'disabled'}
      onClick={() => {
        if (picks?.workspace && picks.repository) {
          addRepository({
            type: 'bitbucket',
            workspace: picks.workspace.slug,
            slug: picks.repository.slug,
            // Which connection the repository was listed through, so every later call about it
            // authenticates as the same Bitbucket account.
            integration_id: selectedId,
          })
        }
      }}
    >
      Connect
    </DefaultButton>
  )
}

const useConnections = (type: Integration['type']) => {
  const { integrations } = useAllIntegrations()
  return useMemo(() => integrations.filter(integration => integration.type === type), [integrations, type])
}

/**
 * A picker over the connections of one type, rendered only when there is a choice to make.
 */
const ConnectionSelect: React.FC<{
  label: string
  connections: Integration[]
  selectedId: string | undefined
  onSelect: (id: string) => void
}> = ({ label, connections, selectedId, onSelect }) =>
  connections.length > 1 ? (
    <SelectBox
      label={label}
      value={selectedId}
      onValueChange={onSelect}
      options={connections.map(connection => ({ value: connection.id, label: connection.name }))}
    />
  ) : null

const useGitLabPicks = () => {
  const form = useFormContext()
  const chosenId = useSelector(form.store, s => s.values.selectedGitLabIntegrationId)
  const picks = useSelector(form.store, s => s.values.gitLabPicks)
  return useConnectionPicks('gitlab', chosenId, picks)
}

const GitLabConnectButton: React.FC<ConnectButtonProps> = ({ addRepository }) => {
  const { selectedId, picks } = useGitLabPicks()

  return (
    <DefaultButton
      buttonType="primary"
      state={picks?.project ? 'default' : 'disabled'}
      onClick={() => {
        if (picks?.project) {
          addRepository({
            type: 'gitlab',
            name: picks.project.path,
            namespace: picks.project.namespace_full_path,
            // Which connection the repository was listed through. The backend clones from that
            // connection's server and records it, so every later call about this repository
            // reaches the same GitLab.
            integration_id: selectedId,
          })
        }
      }}
    >
      Connect
    </DefaultButton>
  )
}

const GitLabFields: React.FC = () => {
  const form = useFormContext()
  const { connections, selectedId, picks } = useGitLabPicks()

  return (
    <>
      <ConnectionSelect
        label="GitLab connection"
        connections={connections}
        selectedId={selectedId}
        onSelect={value => {
          // The projects listed belong to the previous connection, so the pick is cleared along
          // with the choice rather than left pointing at a project on another server.
          form.setFieldValue('selectedGitLabIntegrationId', value)
          form.setFieldValue('gitLabPicks', undefined)
        }}
      />
      <SearchPicker
        key={selectedId}
        label="Available projects"
        placeholder="Search GitLab projects..."
        isDisabled={selectedId === undefined}
        noun={{ singular: 'project', plural: 'projects' }}
        initialValue={picks?.project}
        onChange={project =>
          form.setFieldValue(
            'gitLabPicks',
            selectedId === undefined ? undefined : { integrationId: selectedId, project }
          )
        }
        useSearch={useGitLabProjectSearch}
        searchInput={{ integrationId: selectedId }}
        toOption={project => ({
          value: `${project.namespace_full_path}/${project.path}`,
          label: project.name_with_namespace,
          enabled: project.pixee_id === undefined,
          checked: project.pixee_id !== undefined,
        })}
        side="bottom"
        avoidCollisions={false}
      />
    </>
  )
}

const GenericGitFields: React.FC = () => {
  const form = useFormContext()
  const url = useSelector(form.store, s => s.values.url)
  const username = useSelector(form.store, s => s.values.username)
  const password = useSelector(form.store, s => s.values.password)

  return (
    <>
      <TextInput
        label="URL"
        id="repository-url"
        name="url"
        placeholder="ex: https://scm.com/org/repo-lang.git"
        value={url}
        onChange={value => form.setFieldValue('url', value)}
        isRequired
      />
      <TextInput
        label="Username"
        id="repository-username"
        name="username"
        placeholder="Enter username"
        value={username}
        onChange={value => form.setFieldValue('username', value)}
        explicitLabelCondition="optional"
      />
      <TextInput
        type="password"
        label="Password"
        id="repository-password"
        name="password"
        placeholder="Enter password"
        value={password}
        onChange={value => form.setFieldValue('password', value)}
        explicitLabelCondition="optional"
      />
    </>
  )
}

const GenericGitConnectButton: React.FC<ConnectButtonProps> = ({ addRepository }) => {
  const form = useFormContext()
  const url = useSelector(form.store, s => s.values.url)
  const username = useSelector(form.store, s => s.values.username)
  const password = useSelector(form.store, s => s.values.password)

  const extractRepoName = (repoUrl: string): string => {
    const match = repoUrl.match(/\/([^/]+)\.git$/)
    return match ? (match[1] ?? repoUrl) : repoUrl
  }

  return (
    <DefaultButton
      buttonType="primary"
      state={url ? 'default' : 'disabled'}
      onClick={() =>
        addRepository(
          {
            type: 'git',
            name: extractRepoName(url),
            credentials: { username, password },
            url,
          },
          {
            onSuccess: () => {
              form.setFieldValue('url', '')
              form.setFieldValue('username', '')
              form.setFieldValue('password', '')
            },
          }
        )
      }
    >
      Connect
    </DefaultButton>
  )
}
